import React, { useEffect, useMemo, useState } from 'react';
import { Apple, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  getAppVersionFieldErrors,
  useUpdateAppVersionMutation,
} from '@/client/api/app-version/app-version.mutation';
import { AppPlatform, AppVersionConfig, AppVersionFieldErrors } from '@/client/types/app-version';
import {
  APP_VERSION_DEFAULTS,
  PLATFORM_SPECS,
  PlatformDraft,
  buildPlatformPayload,
  describeRollout,
  isPlatformDirty,
  toPlatformDraft,
  validatePlatformDraft,
} from '../app-version-form';
import { FormField } from './FormField';

interface PlatformCardProps {
  platform: AppPlatform;
  config: AppVersionConfig;
}

export const PlatformCard: React.FC<PlatformCardProps> = ({ platform, config }) => {
  const spec = PLATFORM_SPECS[platform];
  const saved = useMemo(() => toPlatformDraft(config, platform), [config, platform]);
  // Baseline is what the edits are diffed against, so only this admin's changes are sent.
  const [baseline, setBaseline] = useState<PlatformDraft>(saved);
  const [draft, setDraft] = useState<PlatformDraft>(saved);
  const [serverErrors, setServerErrors] = useState<AppVersionFieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const mutation = useUpdateAppVersionMutation();

  const dirty = isPlatformDirty(baseline, draft);

  // Adopt a refetched config (e.g. another admin's save) unless mid-edit.
  useEffect(() => {
    if (!dirty) {
      setBaseline(saved);
      setDraft(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const clientErrors = validatePlatformDraft(platform, draft);
  const fieldError = (field: string): string | undefined => {
    const key = `${platform}.${field}`;
    if (serverErrors[key]) return serverErrors[key];
    if (attempted || draft[field] !== baseline[field]) return clientErrors[key];
    return undefined;
  };
  const knownKeys = new Set(Object.keys(draft).map((f) => `${platform}.${f}`));
  const otherErrors = Object.entries(serverErrors).filter(([key]) => !knownKeys.has(key));

  const setField = (field: string) => (value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setServerErrors((prev) => {
      const key = `${platform}.${field}`;
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = () => {
    setAttempted(true);
    if (Object.keys(clientErrors).length > 0) return;
    mutation.mutate(buildPlatformPayload(platform, baseline, draft), {
      onSuccess: (next) => {
        const fresh = toPlatformDraft(next, platform);
        setBaseline(fresh);
        setDraft(fresh);
        setServerErrors({});
        setAttempted(false);
        toast.success(`${spec.label} update settings saved`);
      },
      onError: (error) => {
        setServerErrors(getAppVersionFieldErrors(error));
        toast.error(getApiErrorMessage(error, `Could not save ${spec.label} settings`));
      },
    });
  };

  const handleReset = () => {
    setDraft(baseline);
    setServerErrors({});
    setAttempted(false);
  };

  const rollout = describeRollout(platform, draft);
  const id = (field: string) => `app-version-${platform}-${field}`;
  const placeholder = (field: string) => APP_VERSION_DEFAULTS[platform][field];
  const Icon = platform === 'ios' ? Apple : Smartphone;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5 text-gray-500" />
          {spec.label}
        </CardTitle>
        <CardDescription>
          {rollout ?? 'Fix the thresholds below to see who gets prompted.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Thresholds</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              id={id('min_version')}
              label="Minimum version"
              hint="Used only when the app sends no build number."
              value={draft.min_version}
              onChange={setField('min_version')}
              placeholder={placeholder('min_version')}
              error={fieldError('min_version')}
            />
            <FormField
              id={id('latest_version')}
              label="Latest version"
              value={draft.latest_version}
              onChange={setField('latest_version')}
              placeholder={placeholder('latest_version')}
              error={fieldError('latest_version')}
            />
            <FormField
              id={id(spec.minCode)}
              label={`Minimum ${spec.codeLabel}`}
              hint={`${spec.codeHint}. Anything lower is forced to update.`}
              value={draft[spec.minCode]}
              onChange={setField(spec.minCode)}
              placeholder={placeholder(spec.minCode)}
              error={fieldError(spec.minCode)}
              inputMode="numeric"
            />
            <FormField
              id={id(spec.latestCode)}
              label={`Latest ${spec.codeLabel}`}
              hint="Anything lower gets a dismissible prompt."
              value={draft[spec.latestCode]}
              onChange={setField(spec.latestCode)}
              placeholder={placeholder(spec.latestCode)}
              error={fieldError(spec.latestCode)}
              inputMode="numeric"
            />
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Store links</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              id={id('store_url')}
              label={spec.storeLabel}
              value={draft.store_url}
              onChange={setField('store_url')}
              placeholder={placeholder('store_url')}
              error={fieldError('store_url')}
              className={platform === 'ios' ? 'sm:col-span-2' : undefined}
            />
            {platform === 'android' && (
              <FormField
                id={id('fallback_url')}
                label="Web fallback URL"
                hint="Opened when the phone can't handle market:// links."
                value={draft.fallback_url}
                onChange={setField('fallback_url')}
                placeholder={placeholder('fallback_url')}
                error={fieldError('fallback_url')}
              />
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Update screen copy</h3>
          <FormField
            id={id('title')}
            label="Title"
            value={draft.title}
            onChange={setField('title')}
            placeholder={placeholder('title')}
            error={fieldError('title')}
          />
          <FormField
            id={id('message')}
            label="Message"
            value={draft.message}
            onChange={setField('message')}
            placeholder={placeholder('message')}
            error={fieldError('message')}
            multiline
          />
          <FormField
            id={id('release_notes')}
            label="Release notes"
            hint="One item per line; the app keeps the line breaks."
            value={draft.release_notes}
            onChange={setField('release_notes')}
            placeholder={placeholder('release_notes')}
            error={fieldError('release_notes')}
            multiline
          />
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
          {otherErrors.length > 0 && (
            <p className="mr-auto text-xs text-red-600">
              {otherErrors.map(([key, message]) => `${key}: ${message}`).join(' · ')}
            </p>
          )}
          <Button variant="outline" onClick={handleReset} disabled={!dirty || mutation.isPending}>
            Reset
          </Button>
          <PermissionButton
            code={PERMISSIONS.system.settings}
            onClick={handleSave}
            disabled={!dirty || mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save {spec.label}
          </PermissionButton>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlatformCard;
